import { useMemo } from 'react';
import type { MediaDocType, MediaLinkDocType } from '@ast-data/shared';
import { useDocs } from './useOwnerMedia';
import { coveredPhotoIds, mediaShownBy } from './mediaLinks';
import { previewOf } from './previewChoice';

/**
 * The preview of each description of a project, by owner id — the picture
 * someone starred, else the first it shows — for a card on a field list. One subscription for the whole project
 * instead of a query per card. A photo a sketch was drawn over is skipped: the
 * sketch shows it already.
 */
export function useProjectPreviews(
    projectId: string
): Map<string, MediaDocType> {
    const ownMedia = useDocs<MediaDocType>('media', { projectId });
    const linksHere = useDocs<MediaLinkDocType>('media_links', { projectId });
    const linkedMedia = useDocs<MediaDocType>('media', {
        id: { $in: linksHere.map((link) => link.mediaId) }
    });

    return useMemo(() => {
        const ownByOwner = groupBy(ownMedia, (media) => media.ownerId);
        const linksByOwner = groupBy(linksHere, (link) => link.ownerId);
        const linkedById = new Map(
            linkedMedia.map((media) => [media.id, media])
        );
        const previews = new Map<string, MediaDocType>();
        for (const ownerId of new Set([
            ...ownByOwner.keys(),
            ...linksByOwner.keys()
        ])) {
            const links = linksByOwner.get(ownerId) ?? [];
            const shown = mediaShownBy(
                ownByOwner.get(ownerId) ?? [],
                links,
                links.flatMap((link) => linkedById.get(link.mediaId) ?? [])
            ).map(({ media }) => media);
            const covered = coveredPhotoIds(shown);
            const preview = previewOf(
                shown.filter((media) => !covered.has(media.id))
            );
            if (preview) {
                previews.set(ownerId, preview);
            }
        }
        return previews;
    }, [ownMedia, linksHere, linkedMedia]);
}

function groupBy<T>(items: readonly T[], keyOf: (item: T) => string) {
    const groups = new Map<string, T[]>();
    for (const item of items) {
        const group = groups.get(keyOf(item));
        if (group) {
            group.push(item);
        } else {
            groups.set(keyOf(item), [item]);
        }
    }
    return groups;
}
